<template>
    <div class="main-content">
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"> <router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Orçamentos</li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="mb-0 fw-bold">Orçamentos</h4>
                    <span class="text-muted fs-7">Exibindo {{ quotes.length }} de {{ totalItems }}</span>
                </div>

                <div class="d-flex gap-2 align-items-center">
                    <transition name="fade">
                        <div v-if="selectedIds.length > 0" class="d-flex align-items-center me-2 pe-3 border-end">
                            <button class="btn btn-pmg-danger btn-sm px-3"
                                @click="confirmDelete(selectedIds.length, true)">
                                <i class="bi bi-trash3 me-1"></i> Excluir ({{ selectedIds.length }})
                            </button>
                        </div>
                    </transition>

                    <button class="btn btn-pmg-light" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>
                    <button class="btn btn-pmg-light" @click="openReportModal">
                        <i class="bi bi-file-earmark-pdf me-1"></i> Relatório Geral
                    </button>
                    <button class="btn btn-pmg-primary" data-bs-toggle="modal" data-bs-target="#modalQuote"
                        @click="openModal('create')">
                        <i class="bi bi-plus-lg me-1"></i> Novo Orçamento
                    </button>
                </div>
            </div>

            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <!-- align-items-end garante que os botões fiquem alinhados com os inputs, já que temos labels em cima -->
                    <div class="row g-3 align-items-end">

                        <!-- Busca por Cliente ou Documento -->
                        <div class="col-md-4">
                            <label class="form-label text-muted small fw-bold mb-1">Cliente ou Documento</label>
                            <input v-model="filter.search" type="text" class="form-control form-control-pmg"
                                placeholder="Digite o nome, CPF ou CNPJ...">
                        </div>

                        <!-- Select de Status -->
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Status</label>
                            <select v-model="filter.status" class="form-select form-control-pmg">
                                <option value="">Todos</option>
                                <option value="pending">Pendente</option>
                                <option value="approved">Aprovado</option>
                                <option value="rejected">Rejeitado</option>
                            </select>
                        </div>

                        <!-- Vencimento (De) -->
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Válido De</label>
                            <input v-model="filter.valid_from" type="date" class="form-control form-control-pmg">
                        </div>

                        <!-- Vencimento (Até) -->
                        <div class="col-md-2">
                            <label class="form-label text-muted small fw-bold mb-1">Válido Até</label>
                            <input v-model="filter.valid_to" type="date" class="form-control form-control-pmg">
                        </div>

                        <!-- Botões de Ação -->
                        <div class="col-md-2 d-flex gap-2">
                            <button class="btn btn-dark w-100 btn-pmg-dark fw-bold py-3" @click="applyFilter">
                                <i class="bi bi-search"></i> Filtrar
                            </button>
                            <button class="btn btn-outline-danger py-1" @click="clearFilter" title="Limpar Filtros">
                                <i class="bi bi-x-lg"></i>
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 5%">
                                <input type="checkbox" class="form-check-input" @change="toggleAll"
                                    :checked="isAllSelected">
                            </th>
                            <th style="width: 10%">Código</th>
                            <th style="width: 25%">Cliente</th>
                            <th style="width: 10%">Criado em</th>
                            <th style="width: 10%">Validade</th>
                            <th style="width: 12%">Valor Total</th>
                            <th style="width: 20%">Pagamento</th>
                            <th style="width: 10%">Status</th>
                            <th style="width: 8%" class="text-end">Ações</th>
                        </tr>
                    </thead>

                    <tbody v-if="quotes.length > 0">
                        <tr v-for="item in quotes" :key="item.id"
                            :class="{ 'selected-row': selectedIds.includes(item.id) }">

                            <!-- Checkbox -->
                            <td>
                                <input type="checkbox" class="form-check-input" :value="item.id" v-model="selectedIds">
                            </td>
                            <!-- Exibição do Código em formato de "Badge" ou Tag -->
                            <td>
                                <span
                                    class="badge bg-secondary-subtle text-secondary border border-secondary-subtle fw-bold"
                                    style="letter-spacing: 1px;">
                                    {{ item.code }}
                                </span>
                            </td>
                            <!-- Cliente -->
                            <td class="fw-bold text-dark">
                                {{ item.client?.name || 'Cliente N/D' }}
                            </td>

                            <!-- Datas -->
                            <td>{{ formatDate(item.created_at) }}</td>
                            <td>{{ formatDate(item.valid_until) }}</td>

                            <!-- Valor Total -->
                            <td class="fw-bold text-primary">
                                R$ {{ formatCurrency(item.total_amount) }}
                            </td>

                            <!-- Forma de Pagamento com Detalhes -->
                            <td>
                                <span class="fw-medium text-dark">{{ getPaymentMethodName(item.payment_method) }}</span>

                                <div v-if="item.payment_method === 'credit_card' && item.installments > 0"
                                    class="small mt-1" style="font-size: 0.8rem;">
                                    <span class="text-muted">
                                        {{ item.installments }}x de R$ {{ formatCurrency(item.total_amount /
                                            item.installments) }}
                                    </span>

                                    <span v-if="item.interest_amount > 0"
                                        class="badge bg-warning-subtle text-warning border border-warning-subtle ms-1 fw-medium px-2 py-1">
                                        Com Juros
                                    </span>
                                    <span v-else-if="item.installments > 1"
                                        class="badge bg-success-subtle text-success border border-success-subtle ms-1 fw-medium px-2 py-1">
                                        Sem Juros
                                    </span>
                                </div>
                            </td>

                            <!-- Status -->
                            <td>
                                <span class="badge rounded-pill px-3 py-2" :class="getStatusClass(item.status)">
                                    {{ getStatusLabel(item.status) }}
                                </span>
                            </td>

                            <!-- Ações -->
                            <td class="text-end">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"
                                        data-bs-boundary="viewport">
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>

                                    <ul class="dropdown-menu dropdown-menu-end shadow border-0 p-2 rounded-3">
                                        <li>
                                            <a class="dropdown-item rounded-2 d-flex align-items-center gap-2 py-2"
                                                href="#" @click.prevent="openModal('show', item)">
                                                <i class="bi bi-eye text-muted fs-5"></i>
                                                <span class="fw-medium">Visualizar</span>
                                            </a>
                                        </li>
                                        <li>
                                            <a class="dropdown-item rounded-2 d-flex align-items-center gap-2 py-2"
                                                href="#" @click.prevent="generatePDF(item)">
                                                <i class="bi bi-file-earmark-pdf text-dark fs-5"></i>
                                                <span class="fw-medium">Imprimir PDF</span>
                                            </a>
                                        </li>
                                        <li>
                                            <a class="dropdown-item rounded-2 d-flex align-items-center gap-2 py-2"
                                                href="#" @click.prevent="openModal('edit', item)">
                                                <i class="bi bi-pencil text-muted fs-5"></i>
                                                <span class="fw-medium">Editar</span>
                                            </a>
                                        </li>
                                        <li>
                                            <hr class="dropdown-divider opacity-50">
                                        </li>
                                        <li>
                                            <a class="dropdown-item rounded-2 d-flex align-items-center gap-2 py-2 text-danger"
                                                href="#" @click.prevent="confirmDelete(item, false)">
                                                <i class="bi bi-trash3 fs-5"></i>
                                                <span class="fw-medium">Excluir</span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>

                    <!-- Estado Vazio -->
                    <tbody v-else>
                        <tr>
                            <!-- Colspan ajustado para 8 (o total de colunas exato) -->
                            <td colspan="8" class="text-center py-5 text-muted">
                                <i class="bi bi-inbox fs-1 mb-2 opacity-50"></i><br>
                                <span>Nenhum orçamento encontrado.</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-center mt-4" v-if="hasMore">
                <button class="btn btn-pmg-light px-5 btn-sm fw-bold" @click="loadMore" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    {{ loading ? 'Carregando...' : 'Carregar mais itens' }}
                </button>
            </div>
        </div>

        <div class="modal fade" id="modalQuote" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-xl modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4">
                        <h5 class="fw-bold m-0">{{ isViewing ? 'Visualizar Orçamento' : (isEditing ? 'Editar Orçamento'
                            : 'Novo Orçamento') }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>

                    <div class="modal-body px-4">
                        <div class="row g-3">
                            <div class="col-md-4 position-relative">
                                <label class="form-label small fw-bold text-muted">Cliente</label>
                                <div class="position-relative">
                                    <i
                                        class="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                    <input type="text" v-model="clientSearchQuery" @input="onClientInput"
                                        @focus="showClientDropdown = true" @blur="hideClientDropdown"
                                        class="form-control form-control-pmg ps-5" placeholder="Buscar cliente...">
                                </div>
                                <div v-if="showClientDropdown && clientSearchResults.length > 0"
                                    class="autocomplete-dropdown shadow-lg rounded-3 border bg-white position-absolute w-100 mt-1"
                                    style="z-index: 1060;">
                                    <ul class="list-unstyled mb-0">
                                        <li v-for="client in clientSearchResults" :key="client.id"
                                            @mousedown="selectClient(client)"
                                            class="p-3 autocomplete-item border-bottom cursor-pointer">
                                            <div class="fw-bold text-dark">{{ client.name }}</div>
                                            <div class="small text-muted">{{ client.document || client.email ||
                                                'Consumidor' }}</div>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div class="col-md-4">
                                <label class="form-label small fw-bold text-muted">Válido Até</label>
                                <input v-model="form.valid_until" type="date" class="form-control form-control-pmg">
                            </div>
                            <div class="col-md-4">
                                <label class="form-label small fw-bold text-muted">Status</label>
                                <select v-model="form.status" class="form-select form-control-pmg">
                                    <option value="pending">Pendente</option>
                                    <option value="approved">Aprovado</option>
                                    <option value="rejected">Rejeitado</option>
                                </select>
                            </div>
                            <div class="col-12 mt-4">
                                <h6 class="fw-bold border-bottom pb-2">Itens do Orçamento</h6>
                            </div>

                            <div class="col-md-5 position-relative">
                                <label class="form-label small fw-bold text-muted">Buscar Produto</label>
                                <div class="position-relative">
                                    <i
                                        class="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                    <input type="text" v-model="productSearchQuery" @input="onProductInput"
                                        @focus="showProductDropdown = true" @blur="hideProductDropdown"
                                        class="form-control form-control-pmg ps-5"
                                        placeholder="Digite o nome do produto...">
                                </div>
                                <div v-if="showProductDropdown && productSearchResults.length > 0"
                                    class="autocomplete-dropdown shadow-lg rounded-3 border bg-white position-absolute w-100 mt-1"
                                    style="z-index: 1060;">
                                    <ul class="list-unstyled mb-0">
                                        <li v-for="product in productSearchResults" :key="product.id"
                                            @mousedown="selectProduct(product)"
                                            class="p-3 autocomplete-item border-bottom cursor-pointer d-flex justify-content-between align-items-center">
                                            <div>
                                                <div class="fw-bold text-dark">{{ product.name }}</div>
                                                <div class="small text-muted">SKU: {{ product.sku || 'N/A' }} | Estoque:
                                                    {{ product.stock || 0 }}</div>
                                            </div>
                                            <span
                                                class="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fw-medium fs-7">
                                                R$ {{ formatCurrency(product.price) }}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div class="col-md-3">
                                <div class="d-flex justify-content-between">
                                    <label class="form-label small fw-bold text-muted">Quantidade</label>
                                    <span v-if="currentProduct.product_id" class="small text-primary fw-medium">
                                        Estoque: {{ currentProduct.max_stock }}
                                    </span>
                                </div>
                                <input v-model.number="currentProduct.quantity" type="number" min="1"
                                    :max="currentProduct.max_stock" class="form-control form-control-pmg text-center">
                            </div>
                            <div class="col-md-4 d-flex align-items-end">
                                <button
                                    class="btn btn-secondary w-100 py-2 fw-medium d-flex align-items-center justify-content-center gap-2"
                                    style="background-color: #6c757d; border: none; border-radius: 8px;"
                                    @click.prevent="addProductToForm" :disabled="!currentProduct.product_id">
                                    <i class="bi bi-cart-plus"></i> Adicionar
                                </button>
                            </div>

                            <div class="col-12 mt-3">
                                <!-- Container com altura limitada para os produtos -->
                                <div class="table-responsive rounded-3 border table-scroll-container">
                                    <table class="table table-sm table-pmg mb-0">
                                        <thead class="bg-light sticky-top" style="z-index: 5; top: 0;">
                                            <tr>
                                                <th style="width: 40%">PRODUTO</th>
                                                <th class="text-center">QTD</th>
                                                <th class="text-end">PREÇO UNIT.</th>
                                                <th class="text-end">SUBTOTAL</th>
                                                <th class="text-center">AÇÕES</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in form.products" :key="index">
                                                <td class="fw-medium">{{ item.name }}</td>
                                                <td class="text-center">{{ item.quantity }}</td>
                                                <td class="text-end">R$ {{ formatCurrency(item.unit_price) }}</td>
                                                <td class="text-end fw-bold">R$ {{ formatCurrency(item.quantity *
                                                    item.unit_price) }}</td>
                                                <td class="text-center">
                                                    <button class="btn btn-sm text-danger"
                                                        @click.prevent="removeProduct(index)">
                                                        <i class="bi bi-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div class="col-12 mt-4">
                                <h6 class="fw-bold border-bottom pb-2">Serviços Adicionais (Opcional)</h6>
                            </div>

                            <div class="col-md-5 position-relative">
                                <label class="form-label small fw-bold text-muted">Buscar Serviço</label>
                                <div class="position-relative">
                                    <i
                                        class="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                    <input type="text" v-model="serviceSearchQuery" @input="onServiceInput"
                                        @focus="showServiceDropdown = true" @blur="hideServiceDropdown"
                                        class="form-control form-control-pmg ps-5"
                                        placeholder="Digite o nome do serviço...">
                                </div>
                                <!-- Autocomplete de Serviços -->
                                <div v-if="showServiceDropdown && serviceSearchResults.length > 0"
                                    class="autocomplete-dropdown shadow-lg rounded-3 border bg-white position-absolute w-100 mt-1"
                                    style="z-index: 1060;">
                                    <ul class="list-unstyled mb-0">
                                        <li v-for="service in serviceSearchResults" :key="service.id"
                                            @mousedown="selectService(service)"
                                            class="p-3 autocomplete-item border-bottom cursor-pointer d-flex justify-content-between align-items-center">
                                            <div>
                                                <!-- Ajustado de service.name para service.description -->
                                                <div class="fw-bold text-dark">{{ service.description }}</div>
                                            </div>
                                            <!-- Ajustado de service.price para service.sale_price -->
                                            <span class="badge rounded-pill px-3 py-2 fw-medium fs-7"
                                                style="background-color: #f3e8ff; color: #7e22ce;">
                                                R$ {{ formatCurrency(service.sale_price) }}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div class="col-md-3">
                                <div class="d-flex justify-content-between">
                                    <label class="form-label small fw-bold text-muted">Quantidade / Horas</label>

                                    <!-- Indicador de Estoque do Serviço -->
                                    <span v-if="currentService.service_id" class="small text-purple fw-medium">
                                        <template v-if="currentService.is_unlimited">Ilimitado</template>
                                        <template v-else>Estoque: {{ currentService.max_stock }}</template>
                                    </span>
                                </div>

                                <!-- Input com limite max dinâmico (apenas se não for ilimitado) -->
                                <input v-model.number="currentService.quantity" type="number" min="1"
                                    :max="currentService.is_unlimited ? null : currentService.max_stock"
                                    class="form-control form-control-pmg text-center">
                            </div>

                            <div class="col-md-4 d-flex align-items-end">
                                <button
                                    class="btn btn-secondary w-100 py-2 fw-medium d-flex align-items-center justify-content-center gap-2"
                                    style="background-color: #8b5cf6; border: none; border-radius: 8px;"
                                    @click.prevent="addServiceToForm" :disabled="!currentService.service_id">
                                    <i class="bi bi-tools"></i> Adicionar Serviço
                                </button>
                            </div>

                            <!-- Tabela de Serviços (Só aparece se houver serviços adicionados) -->
                            <div class="col-12 mt-3">
                                <div class="table-responsive rounded-3 border table-scroll-container"
                                    style="max-height: 200px;">
                                    <table class="table table-sm table-pmg mb-0">
                                        <thead class="bg-light sticky-top" style="z-index: 5; top: 0;">
                                            <tr>
                                                <th style="width: 40%">SERVIÇO</th>
                                                <th class="text-center">QTD</th>
                                                <th class="text-end">PREÇO UNIT.</th>
                                                <th class="text-end">SUBTOTAL</th>
                                                <th class="text-center">AÇÕES</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in form.services" :key="index">
                                                <td class="fw-medium">{{ item.name }}</td>
                                                <td class="text-center">{{ item.quantity }}</td>
                                                <td class="text-end">R$ {{ formatCurrency(item.unit_price) }}</td>
                                                <td class="text-end fw-bold">R$ {{ formatCurrency(item.quantity *
                                                    item.unit_price) }}</td>
                                                <td class="text-center">
                                                    <button class="btn btn-sm text-danger"
                                                        @click.prevent="removeService(index)">
                                                        <i class="bi bi-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <!-- NOVO RESUMO DE VALORES (Cole após a div de serviços e antes de Condições de Pagamento) -->
                            <div class="col-12 mt-4 d-flex justify-content-end">
                                <div class="card bg-light border-0 rounded-4 p-3" style="min-width: 350px;">
                                    <table class="table table-sm table-borderless mb-0">
                                        <tbody class="bg-transparent">
                                            <!-- Subtotal de Produtos -->
                                            <tr>
                                                <td class="text-end text-muted fw-medium py-1">Subtotal dos Produtos:
                                                </td>
                                                <td class="text-end fw-bold text-dark py-1" style="width: 150px;">
                                                    R$ {{ formatCurrency(calculateProductsTotal) }}
                                                </td>
                                            </tr>

                                            <!-- Subtotal de Serviços (Só aparece se houver algum) -->
                                            <tr v-if="form.services && form.services.length > 0">
                                                <td class="text-end text-muted fw-medium py-1">Subtotal dos Serviços:
                                                </td>
                                                <td class="text-end fw-bold text-dark py-1">
                                                    R$ {{ formatCurrency(calculateServicesTotal) }}
                                                </td>
                                            </tr>

                                            <!-- Juros de Parcelamento (Se aplicável) -->
                                            <tr
                                                v-if="form.payment_method === 'credit_card' && form.installments > interestConfig.maxFree">
                                                <td class="text-end text-warning fw-medium py-1">Acréscimo de
                                                    Parcelamento:</td>
                                                <td class="text-end fw-bold text-warning py-1">
                                                    R$ {{ formatCurrency(finalGrandTotal - calculateTotal) }}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td colspan="2">
                                                    <hr class="my-2 opacity-25">
                                                </td>
                                            </tr>

                                            <!-- Valor Final -->
                                            <tr>
                                                <td class="text-end fw-bold text-dark py-2 align-middle">VALOR TOTAL
                                                    FINAL:</td>
                                                <td class="text-end fw-bold text-primary fs-5 py-2">
                                                    R$ {{ formatCurrency(finalGrandTotal) }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div class="col-12 mt-4">
                                <h6 class="fw-bold border-bottom pb-2">Condições de Pagamento</h6>
                            </div>

                            <div class="col-md-4">
                                <label class="form-label small fw-bold text-muted">Forma de Pagamento</label>
                                <select v-model="form.payment_method" class="form-select form-control-pmg">
                                    <option value="pix">PIX</option>
                                    <option value="cash">Dinheiro</option>
                                    <option value="debit_card">Cartão de Débito</option>
                                    <option value="credit_card">Cartão de Crédito</option>
                                    <option value="bank_slip">Boleto</option>
                                </select>
                            </div>

                            <!-- Renderiza inputs adicionais caso seja Cartão de Crédito -->
                            <template v-if="form.payment_method === 'credit_card'">

                                <div class="col-md-4">
                                    <label class="form-label small fw-bold text-muted">Máx. de Parcelas Sem
                                        Juros</label>
                                    <!-- v-model ligado diretamente à variável maxFree -->
                                    <input v-model.number="interestConfig.maxFree" type="number" min="1" max="12"
                                        class="form-control form-control-pmg text-center">
                                </div>

                                <div class="col-md-4">
                                    <label class="form-label small fw-bold text-muted">Taxa de Juros a.m. (%)</label>
                                    <!-- v-model ligado diretamente à variável rate -->
                                    <input v-model.number="interestConfig.rate" type="number" step="0.1" min="0"
                                        class="form-control form-control-pmg text-center">
                                </div>

                                <!-- O select desce para a linha debaixo ocupando 100% da tela para não quebrar o layout -->
                                <div class="col-12 mt-3">
                                    <label class="form-label small fw-bold text-muted">Opções de Parcelamento</label>
                                    <select v-model.number="form.installments"
                                        class="form-select form-control-pmg bg-light">
                                        <option v-for="opt in installmentOptions" :key="opt.installments"
                                            :value="opt.installments">
                                            {{ opt.installments }}x de R$ {{ formatCurrency(opt.installmentValue) }}
                                            {{ opt.hasInterest ? '(Com juros)' : (opt.installments === 1 ? '(À vista)' :
                                                '(Sem juros)') }}
                                            - Total: R$ {{ formatCurrency(opt.finalTotal) }}
                                        </option>
                                    </select>
                                </div>
                            </template>
                            <div class="col-12 mt-3">
                                <label class="form-label small fw-bold text-muted">Observações</label>
                                <textarea v-model="form.notes" class="form-control form-control-pmg" rows="2"
                                    placeholder="Notas internas ou para o cliente..."></textarea>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer border-0 pb-4 px-4">
                        <button class="btn btn-pmg-light me-2" data-bs-dismiss="modal">{{ isViewing ? 'Fechar' :
                            'Cancelar' }}</button>
                        <button v-if="!isViewing" class="btn btn-pmg-primary px-4" :disabled="!isFormValid || loading"
                            @click="saveQuote">
                            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                            {{ isEditing ? 'Atualizar' : 'Salvar Orçamento' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal fade" id="confirmDeleteModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4 text-center p-3">
                    <div class="modal-body">
                        <div class="text-danger mb-3"><i class="bi bi-exclamation-triangle-fill fs-1"></i></div>
                        <h5 class="fw-bold">Tem certeza?</h5>
                        <p class="text-muted small">{{ isBulkDelete ? `Excluir ${selectedIds.length} orçamentos?` :
                            `Excluir este orçamento?` }}</p>
                    </div>
                    <div class="d-flex gap-2 justify-content-center">
                        <button class="btn btn-pmg-light px-4" data-bs-dismiss="modal">Não</button>
                        <button class="btn btn-danger px-4" @click="executeDeletion">Sim, Excluir</button>
                    </div>
                </div>
            </div>
        </div>
        <!-- MODAL RELATÓRIO GERAL (IDÊNTICO AO PADRÃO DA TELA DE PRODUTOS) -->
        <div class="modal fade" id="reportGeneralModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg"> <!-- modal-lg para dar mais respiro aos inputs -->
                <div class="modal-content border-0 shadow-lg" style="border-radius: 12px;">

                    <div class="modal-header border-0 pt-4 px-4 pb-2">
                        <h5 class="modal-title fw-bold text-dark">Filtros do Relatório Geral</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div class="modal-body px-4 pb-4 pt-2">
                        <div class="row g-4"> <!-- g-4 aumenta o espaçamento entre as linhas e colunas -->

                            <!-- LINHA 1 -->
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted mb-2">Criação (Inicial)</label>
                                <input type="date" class="form-control py-2 rounded-3 text-secondary"
                                    v-model="reportFilters.created_from">
                            </div>
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted mb-2">Criação (Final)</label>
                                <input type="date" class="form-control py-2 rounded-3 text-secondary"
                                    v-model="reportFilters.created_to">
                            </div>

                            <!-- LINHA 2 -->
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted mb-2">Validade (Inicial)</label>
                                <input type="date" class="form-control py-2 rounded-3 text-secondary"
                                    v-model="reportFilters.valid_from">
                            </div>
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted mb-2">Validade (Final)</label>
                                <input type="date" class="form-control py-2 rounded-3 text-secondary"
                                    v-model="reportFilters.valid_to">
                            </div>

                            <!-- LINHA 3 -->
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted mb-2">Valor Mínimo (R$)</label>
                                <!-- type="text" é obrigatório para aceitar a máscara -->
                                <input type="text" class="form-control py-2 rounded-3 text-secondary" placeholder="0,00"
                                    :value="formatCurrencyInput(reportFilters.min_value)"
                                    @input="handleCurrencyInput($event, 'min_value')">
                            </div>
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted mb-2">Valor Máximo (R$)</label>
                                <input type="text" class="form-control py-2 rounded-3 text-secondary" placeholder="0,00"
                                    :value="formatCurrencyInput(reportFilters.max_value)"
                                    @input="handleCurrencyInput($event, 'max_value')">
                            </div>

                            <!-- LINHA 4 -->
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted mb-2">Status do Orçamento</label>
                                <select class="form-select py-2 rounded-3 text-secondary"
                                    v-model="reportFilters.status">
                                    <option value="ALL">Todos os Status</option>
                                    <option value="pending">Pendente</option>
                                    <option value="approved">Aprovado</option>
                                    <option value="rejected">Rejeitado</option>
                                </select>
                            </div>

                            <!-- BOTÃO GERAR (Embutido no final do body para ficar colado nos filtros igual na imagem) -->
                            <div class="col-12 mt-4 pt-2">
                                <button type="button"
                                    class="btn w-100 fw-bold py-3 rounded-3 shadow-sm d-flex justify-content-center align-items-center"
                                    style="background-color: #561569; color: white; border: none; letter-spacing: 0.5px;"
                                    @click="generateGeneralReport" :disabled="isGeneratingReport">

                                    <span v-if="isGeneratingReport" class="spinner-border spinner-border-sm me-2"
                                        role="status"></span>

                                    <template v-else>
                                        <i class="bi bi-file-earmark-pdf-fill me-2 fs-5"></i>
                                        GERAR RELATÓRIO PDF
                                    </template>

                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    inject: ['globalLoading'],
    data() {
        return {
            quotes: [],
            alerts: [],
            // Autocomplete Variables
            clientSearchQuery: '',
            clientSearchResults: [],
            showClientDropdown: false,
            clientDebounce: null,
            productSearchQuery: '',
            productSearchResults: [],
            showProductDropdown: false,
            productDebounce: null,
            form: { id: null, client_id: '', status: 'pending', valid_until: '', notes: '', products: [], services: [], payment_method: 'pix', installments: 1, interest_amount: 0 },
            selectedIds: [],
            filter: {
                search: '',
                status: '',
                valid_from: '',
                valid_to: ''
            },
            totalItems: 0,
            currentPage: 1,
            lastPage: 1,
            loading: false,
            hasMore: false,
            showFilter: false,
            itemToDelete: null,
            isBulkDelete: false,
            currentProduct: { product_id: '', name: '', quantity: 1, unit_price: 0, max_stock: 0 },
            isEditing: false,
            isViewing: false,
            interestConfig: {
                maxFree: 3,  // Até 3x sem juros
                rate: 2.5    // Taxa de 2.5% ao mês nas parcelas seguintes
            },
            reportModalInstance: null,
            isGeneratingReport: false,
            reportFilters: {
                created_from: '',
                created_to: '',
                valid_from: '',
                valid_to: '',
                status: 'ALL',
                min_value: '',
                max_value: ''
            },
            serviceSearchQuery: '',
            serviceSearchResults: [],
            showServiceDropdown: false,
            serviceDebounce: null,
            currentService: {
                service_id: '',
                name: '',
                quantity: 1,
                unit_price: 0,
                max_stock: 0,
                is_unlimited: true
            },
        }
    },
    computed: {
        isAllSelected() { return this.quotes.length > 0 && this.selectedIds.length === this.quotes.length; },
        isFormValid() { return this.form.client_id && this.form.valid_until && this.form.products.length > 0; },
        installmentOptions() {
            const options = [];
            const baseTotal = this.calculateTotal;
            if (baseTotal <= 0) return options;

            for (let n = 1; n <= 12; n++) {
                if (n <= this.interestConfig.maxFree) {
                    options.push({
                        installments: n,
                        installmentValue: baseTotal / n,
                        finalTotal: baseTotal,
                        hasInterest: false
                    });
                } else {
                    // Cálculo Tabela Price (Juros Compostos padrão de maquininha)
                    const i = this.interestConfig.rate / 100;
                    const pmt = baseTotal * (i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);

                    options.push({
                        installments: n,
                        installmentValue: pmt,
                        finalTotal: pmt * n,
                        hasInterest: true
                    });
                }
            }
            return options;
        },

        // Pega os detalhes da parcela selecionada atualmente
        selectedInstallmentDetails() {
            if (this.form.payment_method !== 'credit_card') return null;
            return this.installmentOptions.find(opt => opt.installments === this.form.installments);
        },

        // O verdadeiro valor final (Produtos + Juros)
        finalGrandTotal() {
            if (this.selectedInstallmentDetails) {
                return this.selectedInstallmentDetails.finalTotal;
            }
            return this.calculateTotal;
        },
        calculateProductsTotal() {
            return this.form.products.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0);
        },

        calculateServicesTotal() {
            return (this.form.services || []).reduce((acc, item) => acc + (item.quantity * item.unit_price), 0);
        },

        calculateTotal() {
            // O total base agora é a soma dos dois subtotais
            return this.calculateProductsTotal + this.calculateServicesTotal;
        },
    },
    mounted() {
        const modalElement = document.getElementById('modalQuote');
        const reportModalInstance = document.getElementById('reportGeneralModal');
        if (modalElement) {
            this.modalInstance = new window.bootstrap.Modal(modalElement);
        }
        if (reportModalInstance) {
            this.reportModalInstance = new window.bootstrap.Modal(reportModalInstance);
        }
        this.fetchQuotes();
    },
    watch: {
        'currentProduct.product_id'(newId) {
            if (newId) {
                const prod = this.productSearchResults.find(p => p.id === newId);
                if (prod) this.currentProduct.unit_price = prod.price;
            }
        },
        'form.payment_method'(newMethod, oldMethod) {
            if (oldMethod && newMethod !== 'credit_card') {
                this.form.installments = 1;
            }
        },
        'currentService.service_id'(newId) {
            if (newId) {
                const serv = this.serviceSearchResults.find(s => s.id === newId);
                // Usa sale_price em vez de price
                if (serv) this.currentService.unit_price = serv.sale_price;
            }
        },
    },
    methods: {
        async fetchQuotes(page = 1, append = false) {
            this.globalLoading.show('Carregando orçamentos...');
            this.loading = true;
            try {
                const response = await api.get('/quotes', {
                    params: {
                        page,
                        ...this.filter
                    }
                });

                const { data, meta } = response.data;
                this.quotes = append ? [...this.quotes, ...data] : data;
                this.totalItems = meta.total;
                this.currentPage = meta.current_page;
                this.lastPage = meta.last_page;
                this.hasMore = this.currentPage < this.lastPage;
            } catch (e) {
                this.showAlert('Erro ao buscar dados.', 'error');
            } finally {
                this.loading = false;
                this.globalLoading.hide();
            }
        },

        // --- CLIENT AUTOCOMPLETE ---
        onClientInput() {
            this.showClientDropdown = true;
            clearTimeout(this.clientDebounce);
            if (this.clientSearchQuery.length < 2) return;
            this.clientDebounce = setTimeout(async () => {
                const res = await api.get('/clients', { params: { search: this.clientSearchQuery, limit: 8 } });
                this.clientSearchResults = res.data.data || res.data;
            }, 300);
        },
        selectClient(client) {
            this.form.client_id = client.id;
            this.clientSearchQuery = client.name;
            this.showClientDropdown = false;
        },
        hideClientDropdown() { setTimeout(() => this.showClientDropdown = false, 200); },

        // --- PRODUCT AUTOCOMPLETE ---
        onProductInput() {
            this.showProductDropdown = true;
            clearTimeout(this.productDebounce);
            if (this.productSearchQuery.length < 2) return;
            this.productDebounce = setTimeout(async () => {
                const res = await api.get('/products', { params: { search: this.productSearchQuery, limit: 8 } });
                this.productSearchResults = res.data.data || res.data;
            }, 300);
        },
        selectProduct(product) {
            this.currentProduct = {
                product_id: product.id,
                name: product.name,
                quantity: 1,
                unit_price: product.price,
                max_stock: product.stock || 0 // Salva o estoque disponível
            };
            this.productSearchQuery = product.name;
            this.showProductDropdown = false;
        },
        hideProductDropdown() { setTimeout(() => this.showProductDropdown = false, 200); },

        addProductToForm() {
            if (!this.currentProduct.product_id || this.currentProduct.quantity <= 0) return;
            const idx = this.form.products.findIndex(p => p.product_id === this.currentProduct.product_id);
            const currentCartQuantity = idx > -1 ? this.form.products[idx].quantity : 0;
            const projectedQuantity = currentCartQuantity + this.currentProduct.quantity;
            if (projectedQuantity > this.currentProduct.max_stock) {
                this.showAlert(`Quantidade indisponível! Estoque atual: ${this.currentProduct.max_stock}`, 'error');
                return;
            }

            if (idx > -1) {
                this.form.products[idx].quantity += this.currentProduct.quantity;
            } else {
                this.form.products.push({ ...this.currentProduct });
            }
            this.currentProduct = { product_id: '', name: '', quantity: 1, unit_price: 0, max_stock: 0 };
            this.productSearchQuery = '';
        },
        removeProduct(index) { this.form.products.splice(index, 1); },

        async saveQuote() {
            this.loading = true;

            // Vincula as taxas configuradas ao formulário antes de enviar
            this.form.max_free = this.interestConfig.maxFree;
            this.form.interest_rate = this.interestConfig.rate;
            this.form.interest_amount = this.finalGrandTotal - this.calculateTotal;

            try {
                // Ajustado para usar o form.id em vez de isEditing, prevenindo que um registro existente tente criar um novo post
                if (this.form.id) {
                    await api.put(`/quotes/${this.form.id}`, this.form);
                } else {
                    await api.post('/quotes', this.form);
                }

                this.showAlert('Orçamento salvo com sucesso!');
                this.fetchQuotes();
                this.modalInstance.hide();
            } catch (e) {
                if (e.response?.status === 422) {

                    const validationErrors = e.response.data?.data || e.response.data?.errors;
                    this.errors = validationErrors || {};
                    if (!validationErrors || Object.keys(validationErrors).length === 0) {
                        this.showAlert("Erro de validação. Verifique os campos preenchidos.", "error");
                        return;
                    }
                    let mensagensErro = "Os seguintes campos precisam de correção:\n\n";
                    for (const campo in validationErrors) {
                        mensagensErro += `- ${validationErrors[campo][0]}\n`;
                    }

                    this.showAlert(mensagensErro, "error");

                } else {
                    console.error("Erro desconhecido:", e);
                    this.showAlert("Erro ao salvar.", "error");
                }
            } finally {
                this.loading = false;
            }
        },

        async executeDeletion() {
            try {
                if (this.isBulkDelete) await api.post('/quotes/bulk-delete', { ids: this.selectedIds });
                else await api.delete(`/quotes/${this.itemToDelete.id}`);
                this.showAlert('Removido!');
                this.fetchQuotes();
                this.selectedIds = [];
            } catch (e) { this.showAlert('Erro ao excluir.', 'error'); }
            finally {
                const m = window.bootstrap.Modal.getInstance(document.getElementById('confirmDeleteModal'));
                if (m) m.hide();
            }
        },

        resetForm() {
            // Retorna o formulário ao estado inicial
            this.form = {
                id: null,
                client_id: '',
                status: 'pending',
                valid_until: new Date().toISOString().split('T')[0],
                notes: '',
                payment_method: 'pix',
                installments: 1,
                interest_amount: 0,
                max_free: 3,        // Valor padrão inicial
                interest_rate: 2.5, // Valor padrão inicial
                products: []
            };

            // Limpa as variáveis de busca e interface
            this.clientSearchQuery = '';
            this.clientSearchResults = [];
            this.productSearchQuery = '';
            this.productSearchResults = [];
            this.isEditing = false;
            this.isViewing = false;

            // Reseta o item de produto que estava sendo digitado
            this.currentProduct = {
                product_id: '',
                name: '',
                quantity: 1,
                unit_price: 0,
                max_stock: 0
            };
            this.form.services = [];
            this.serviceSearchQuery = '';
            this.serviceSearchResults = [];
            this.currentService = { service_id: '', name: '', quantity: 1, unit_price: 0 };
        },

        openModal(mode, item = null) {
            this.isEditing = mode === 'edit';
            this.isViewing = mode === 'show';

            if (item) {
                // Carrega as taxas salvas, com fallback para o padrão
                this.interestConfig.maxFree = item.max_free !== undefined ? item.max_free : 3;
                this.interestConfig.rate = item.interest_rate !== undefined ? item.interest_rate : 2.5;

                this.form = {
                    id: item.id,
                    client_id: item.client_id,
                    status: item.status,
                    valid_until: item.valid_until,
                    notes: item.notes,
                    payment_method: item.payment_method || 'pix',
                    installments: item.installments || 1,
                    interest_amount: item.interest_amount || 0,

                    // Mapeamento seguro de Produtos (lê item.products ou item.items)
                    products: (item.products || item.items || []).map(i => ({
                        product_id: i.id || i.product_id,
                        name: i.name || i.description || 'Produto',
                        quantity: i.pivot?.quantity ?? i.quantity ?? 1,
                        unit_price: i.pivot?.unit_price ?? i.unit_price ?? 0
                    })),

                    // Mapeamento seguro de Serviços
                    services: (item.services || []).map(i => ({
                        service_id: i.id || i.service_id,
                        name: i.description || i.name || 'Serviço',
                        quantity: i.pivot?.quantity ?? i.quantity ?? 1,
                        unit_price: i.pivot?.unit_price ?? i.unit_price ?? 0
                    }))
                };

                this.clientSearchQuery = item.client?.name || '';
            } else {
                this.resetForm();
            }

            if (this.modalInstance) this.modalInstance.show();
        },
        confirmDelete(target, bulk = false) {
            this.itemToDelete = target; this.isBulkDelete = bulk;
            new window.bootstrap.Modal(document.getElementById('confirmDeleteModal')).show();
        },
        formatCurrency(v) { return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }); },
        formatDate(d) { return d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'; },
        getStatusLabel(s) { return { pending: 'Pendente', approved: 'Aprovado', rejected: 'Rejeitado' }[s] || s; },
        getStatusClass(s) { return { pending: 'bg-warning-subtle text-warning', approved: 'bg-success-subtle text-success', rejected: 'bg-danger-subtle text-danger' }[s] || 'bg-light'; },
        applyFilter() { this.fetchQuotes(1, false); },
        loadMore() { if (this.hasMore) this.fetchQuotes(this.currentPage + 1, true); },
        toggleAll() { this.selectedIds = this.isAllSelected ? [] : this.quotes.map(q => q.id); },
        showAlert(message, type = 'success') {
            const id = Date.now();
            const themeClass = type === 'error' ? 'danger' : type;
            let icon = 'bi-check-circle-fill';
            if (themeClass === 'danger') icon = 'bi-x-circle-fill';
            if (type === 'warning') icon = 'bi-exclamation-triangle-fill';
            this.alerts.push({ id, message, type: themeClass, icon });
            const timeout = themeClass === 'danger' ? 6000 : 4000;
            setTimeout(() => this.removeAlert(id), timeout);
        },
        removeAlert(id) { this.alerts = this.alerts.filter(a => a.id !== id); },
        async generatePDF(item) {
            try {
                this.showAlert('Gerando PDF, aguarde...', 'info');
                const response = await api.get(`/quotes/${item.id}/print`, {
                    responseType: 'blob'
                });

                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = url;
                const fileName = `orcamento_${String(item.id).padStart(5, '0')}.pdf`;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Erro ao baixar o PDF:', error);
                this.showAlert('Erro ao gerar o PDF do orçamento.', 'error');
            }
        },
        getPaymentMethodName(method) {
            const methods = {
                'pix': 'PIX',
                'cash': 'Dinheiro',
                'credit_card': 'Cartão de Crédito',
                'debit_card': 'Cartão de Débito',
                'bank_slip': 'Boleto'
            };
            return methods[method] || 'Não Informado';
        },
        clearFilter() {
            this.filter = {
                search: '',
                status: '',
                valid_from: '',
                valid_to: ''
            };
            this.fetchQuotes();
        },

        openReportModal() {
            // Reseta os filtros padrão toda vez que abrir
            this.reportFilters = {
                created_from: '', created_to: '', valid_from: '', valid_to: '',
                status: 'ALL', min_value: '', max_value: ''
            };
            this.reportModalInstance.show();
        },

        async generateGeneralReport() {
            try {
                this.isGeneratingReport = true;

                // Faz a requisição enviando os filtros do modal
                const response = await api.get('/quotes/report/general', {
                    params: this.reportFilters,
                    responseType: 'blob' // Necessário para arquivos
                });

                // Lógica padrão de download (igual ao imprimir orçamento)
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = url;

                // Pega o nome do arquivo se a API enviar pelo header, senão usa um padrão
                const contentDisposition = response.headers['content-disposition'];
                let fileName = 'relatorio_orcamentos.pdf';
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
                    if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
                }

                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                this.reportModalInstance.hide();
                this.showAlert('Relatório gerado com sucesso!', 'success');

            } catch (error) {
                console.error(error);
                this.showAlert('Erro ao gerar relatório.', 'error');
            } finally {
                this.isGeneratingReport = false;
            }
        },

        formatCurrencyInput(value) {
            if (value === '' || value === null || value === undefined) return '';
            // Converte o float (ex: 1500.5) para string pt-BR (ex: "1.500,50")
            return Number(value).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },

        // Método 2: Cria a máscara ao vivo enquanto o usuário digita
        handleCurrencyInput(event, field) {
            let input = event.target;
            let value = input.value;

            // 1. Remove tudo que não for dígito numérico
            value = value.replace(/\D/g, "");

            // Se o usuário apagar tudo, limpa a variável e o input
            if (value === "") {
                this.reportFilters[field] = '';
                input.value = '';
                return;
            }

            // 2. Transforma a string de números em decimal (ex: "1234" vira 12.34)
            let numericValue = (parseInt(value, 10) / 100);

            // 3. Formata visualmente com pontos e vírgulas para o usuário ver
            input.value = numericValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });

            // 4. Salva o número "limpo" no filtro para o Laravel receber corretamente
            this.reportFilters[field] = numericValue;
        },
        // --- SERVICE AUTOCOMPLETE ---
        onServiceInput() {
            this.showServiceDropdown = true;
            clearTimeout(this.serviceDebounce);
            if (this.serviceSearchQuery.length < 2) return;
            this.serviceDebounce = setTimeout(async () => {
                // Supondo que a sua rota de serviços seja /services
                const res = await api.get('/services', { params: { search: this.serviceSearchQuery, limit: 8 } });
                this.serviceSearchResults = res.data.data || res.data;
            }, 300);
        },
        selectService(service) {
            this.currentService = {
                service_id: service.id,
                name: service.description,
                quantity: 1,
                unit_price: service.sale_price,
                // Pega o estoque real do JSON (servie.stock). Se não vier, assume 0.
                max_stock: service.stock !== undefined ? service.stock : 0,
                // Força is_unlimited para falso se houver um controle de estoque real, 
                // ou baseia-se estritamente no fato do stock ser maior que zero/controlado
                is_unlimited: false
            };
            this.serviceSearchQuery = service.description;
            this.showServiceDropdown = false;
        },
        hideServiceDropdown() {
            setTimeout(() => this.showServiceDropdown = false, 200);
        },

        addServiceToForm() {
            if (!this.currentService.service_id || this.currentService.quantity <= 0) return;

            // Inicializa o array se não existir
            if (!this.form.services) this.form.services = [];

            const idx = this.form.services.findIndex(s => s.service_id === this.currentService.service_id);

            if (idx > -1) {
                this.form.services[idx].quantity += this.currentService.quantity;
            } else {
                this.form.services.push({ ...this.currentService });
            }

            // Limpa os campos de pesquisa
            this.currentService = { service_id: '', name: '', quantity: 1, unit_price: 0 };
            this.serviceSearchQuery = '';
        },
        removeService(index) {
            this.form.services.splice(index, 1);
        },
    }
}
</script>

<style scoped>
.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.btn-pmg-danger {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
    border-radius: 10px;
    font-weight: 600;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
    transition: all 0.2s;
}

.form-control-pmg:focus {
    border-color: #561569;
    box-shadow: 0 0 0 4px rgba(33, 51, 97, 0.1);
}

.autocomplete-dropdown {
    max-height: 280px;
    overflow-y: auto;
    scrollbar-width: thin;
    z-index: 1100;
}

.autocomplete-item {
    transition: background 0.2s;
    cursor: pointer;
}

.autocomplete-item:hover {
    background-color: #f8fafc;
}

.bg-primary-subtle {
    background-color: #e0f2fe !important;
}

.text-primary {
    color: #0369a1 !important;
}

.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    padding: 1.2rem;
    border-bottom: 1px solid #edf2f7;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s;
}

.slide-fade-enter-from {
    transform: translateX(50px);
    opacity: 0;
}

.slide-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
    border-left: 5px solid #dc2626 !important;
    animation: shake 0.4s ease-in-out;
}

@keyframes shake {

    0%,
    100% {
        transform: translateX(0);
    }

    25% {
        transform: translateX(-5px);
    }

    75% {
        transform: translateX(5px);
    }
}

.table-scroll-container {
    max-height: 350px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
}

.table-scroll-container::-webkit-scrollbar {
    width: 6px;
}

.table-scroll-container::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 10px;
}

.table-scroll-container thead th {
    box-shadow: 0 1px 0 #edf2f7;
}

.dropdown-menu {
    z-index: 1065 !important;
}

.table-responsive {
    overflow: visible !important;
}

.table-scroll-container {
    padding-bottom: 60px;
}

.dropdown-item {
    font-size: 0.9rem;
    color: #334155;
    transition: all 0.2s ease;
}

.dropdown-item:hover {
    background-color: #f8fafc;
    color: #1e293b;
}

.dropdown-item.text-danger:hover {
    background-color: #fff1f2 !important;
    color: #dc2626 !important;
}

.btn-pmg-icon.border {
    border-color: #e2e8f0 !important;
    background: white;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.btn-pmg-icon:hover {
    background-color: #f1f5f9;
}

.table-scroll-container {
    max-height: 280px !important;
    overflow-y: auto !important;
    overflow-x: hidden;
    display: block;
}

.sticky-top {
    position: sticky;
    top: 0;
    background-color: #f8fafc;
    box-shadow: inset 0 -1px 0 #edf2f7;
}

.table-scroll-container::-webkit-scrollbar {
    width: 6px;
}

.table-scroll-container::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 10px;
}

.table-scroll-container::-webkit-scrollbar-track {
    background: #f1f5f9;
}
</style>